import * as Sentry from '@sentry/react';

import { PlatformBridge } from './bridge';

const esperSettingsPackage = 'io.shoonya.shoonyadpc';

export interface DeviceInfo {
  deviceId: string;
  deviceName: string;
  manufacturer: string;
  model: string;
  platform: string;
  operatingSystem: string;
  osVersion: string;
  esperInstalled: boolean;
}

const STORAGE_KEY = 'cibli_web_machine_id';

function getCapacitorDeviceInfo(): Promise<DeviceInfo> {
  return new Promise(resolve => {
    (async () => {
      const plugins = PlatformBridge.capacitorPlugins!;
      const Device = plugins.Device as {
        getId(): Promise<{ identifier: string }>;
        getInfo(): Promise<{
          name?: string;
          manufacturer?: string;
          model?: string;
          platform?: string;
          operatingSystem?: string;
          osVersion?: string;
        }>;
      };

      const PackageInfo = plugins.PackageInfo as
        | {
            isPackageInstalled(options: { packageName: string }): Promise<{ isInstalled: boolean }>;
          }
        | undefined;

      const [deviceId, deviceInfo] = await Promise.all([Device.getId(), Device.getInfo()]);

      const manufacturer = deviceInfo.manufacturer || 'Unknown';
      const boxManufacturer = 'rockchip';
      let esperInstalled: boolean;
      try {
        if (!PackageInfo) {
          throw new Error(
            'PackageInfo plugin not available: apps/interview is ahead of apps/booth-app',
          );
        }
        const packageCheck = await PackageInfo.isPackageInstalled({
          packageName: esperSettingsPackage,
        });
        esperInstalled = packageCheck.isInstalled;
      } catch (error) {
        console.error('Failed to get package info', error);
        Sentry.captureException(error);
        // Fallback: if rockchip, consider esper installed
        esperInstalled = manufacturer.toLowerCase() === boxManufacturer;
      }

      resolve({
        deviceId: deviceId.identifier,
        deviceName: deviceInfo.name || 'Unknown Device',
        manufacturer,
        model: deviceInfo.model || 'Unknown',
        platform: 'capacitor',
        operatingSystem: deviceInfo.operatingSystem || 'Android',
        osVersion: deviceInfo.osVersion || 'Unknown',
        esperInstalled,
      });
    })();
  });
}

// Generates a stable machine ID for web browsers using localStorage and browser fingerprinting
function getStableWebMachineId(): string {
  const storedId = localStorage.getItem(STORAGE_KEY);
  if (storedId) {
    return storedId;
  }

  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    navigator.hardwareConcurrency || 'unknown',
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
  ].join('|');

  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const machineId = `web-${Math.abs(hash).toString(36)}-${Date.now().toString(36)}`;

  try {
    localStorage.setItem(STORAGE_KEY, machineId);
  } catch (e) {
    console.warn('Failed to store machine ID in localStorage', e);
  }

  return machineId;
}

function getWebDeviceInfo(): DeviceInfo {
  const machineId = getStableWebMachineId();

  return {
    deviceId: machineId,
    deviceName: 'Web Browser',
    manufacturer: 'Browser',
    model: navigator.userAgent,
    platform: 'web',
    operatingSystem: navigator.platform, // navigator?.userAgentData?.platform
    osVersion: navigator.userAgent,
    esperInstalled: false,
  };
}

export const DeviceInfoBridge = {
  async getDeviceInfo(): Promise<DeviceInfo> {
    if (PlatformBridge.isCapacitor) {
      return getCapacitorDeviceInfo();
    }
    return getWebDeviceInfo();
  },

  async getMachineId(): Promise<string | null> {
    if (PlatformBridge.isCapacitor) {
      const plugins = PlatformBridge.capacitorPlugins!;
      const Device = plugins.Device as { getId(): Promise<{ identifier: string }> };
      const info = await Device.getId();
      return info.identifier;
    }

    return getStableWebMachineId();
  },

  async getLanguageCode(): Promise<string> {
    if (PlatformBridge.isCapacitor) {
      const plugins = PlatformBridge.capacitorPlugins!;
      const Device = plugins.Device as {
        getLanguageTag(): Promise<{ value: string }>;
      };
      const result = await Device.getLanguageTag();
      return result.value;
    }

    return navigator.language;
  },
};
